import React from 'react';

/**
 * ChatMarkdown
 * ============
 * A small, DEPENDENCY-FREE Markdown renderer for KoneChat assistant replies.
 *
 * The Bedrock/Claude model returns GitHub-flavoured Markdown - pipe tables,
 * headings (#/##/###), bold (**), inline code (`), fenced code blocks (```),
 * and bullet / numbered lists. Rendering that as raw text (the old behaviour)
 * showed literal "|", "**" and "###" in the chat. This parses the common
 * subset into real React elements so tables render as tables, etc.
 *
 * It is intentionally minimal and SAFE: it builds React elements directly (no
 * dangerouslySetInnerHTML), so model output can never inject HTML/scripts.
 */

// --- Inline formatting: **bold**, *italic*, `code`, [text](url) ------------
// Returns an array of strings / React nodes for one line of text.
function renderInline(text, keyPrefix) {
  if (text == null) return null;
  const nodes = [];
  let remaining = String(text);
  let key = 0;

  // Ordered so the earliest match in the string wins.
  const patterns = [
    { re: /`([^`]+)`/, wrap: (m) => <code key={`${keyPrefix}-c-${key++}`} className="cm-code-inline">{m[1]}</code> },
    { re: /\*\*([^*]+)\*\*/, wrap: (m) => <strong key={`${keyPrefix}-b-${key++}`}>{m[1]}</strong> },
    { re: /__([^_]+)__/, wrap: (m) => <strong key={`${keyPrefix}-b2-${key++}`}>{m[1]}</strong> },
    { re: /\*([^*]+)\*/, wrap: (m) => <em key={`${keyPrefix}-i-${key++}`}>{m[1]}</em> },
    {
      re: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/,
      wrap: (m) => (
        <a key={`${keyPrefix}-a-${key++}`} href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a>
      ),
    },
  ];

  while (remaining) {
    let best = null;
    for (const p of patterns) {
      const m = p.re.exec(remaining);
      if (m && (best == null || m.index < best.index)) {
        best = { index: m.index, match: m, wrap: p.wrap };
      }
    }
    if (!best) {
      nodes.push(remaining);
      break;
    }
    if (best.index > 0) nodes.push(remaining.slice(0, best.index));
    nodes.push(best.wrap(best.match));
    remaining = remaining.slice(best.index + best.match[0].length);
  }
  return nodes;
}

// Split a markdown table row "| a | b |" into cells.
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

// A separator row like |---|:--:|---| marks a table header.
function isTableSeparator(line) {
  const s = line.trim();
  if (!s.includes('-')) return false;
  return /^\|?[\s:|-]+\|?$/.test(s) && s.includes('-');
}

function isTableRow(line) {
  return line.trim().startsWith('|') || line.includes(' | ');
}

/**
 * Parse the full markdown text into an array of block React elements.
 */
function renderMarkdown(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    // --- Fenced code block ```lang ... ``` ---
    if (line.trim().startsWith('```')) {
      const buf = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      blocks.push(
        <pre key={`pre-${k++}`} className="cm-code-block"><code>{buf.join('\n')}</code></pre>
      );
      continue;
    }

    // --- Table: a row followed by a separator row ---
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && isTableRow(lines[i]) && lines[i].trim() !== '') {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      // Capture the block index so the map callbacks below don't close over the
      // mutating loop variable `k` (eslint no-loop-func).
      const bk = k++;
      blocks.push(
        <div key={`tbl-${bk}`} className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>{header.map((c, ci) => <th key={ci}>{renderInline(c, `th-${bk}-${ci}`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {header.map((_, ci) => <td key={ci}>{renderInline(r[ci] ?? '', `td-${bk}-${ri}-${ci}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // --- Headings # / ## / ### ---
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const Tag = `h${Math.min(level + 2, 6)}`; // h1->h3 etc so it fits the bubble
      const bk = k++;
      blocks.push(<Tag key={`h-${bk}`} className="cm-heading">{renderInline(h[2], `h-${bk}`)}</Tag>);
      i += 1;
      continue;
    }

    // --- Unordered list ---
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ''));
        i += 1;
      }
      const bk = k++;
      blocks.push(
        <ul key={`ul-${bk}`} className="cm-list">
          {items.map((it, ii) => <li key={ii}>{renderInline(it, `li-${bk}-${ii}`)}</li>)}
        </ul>
      );
      continue;
    }

    // --- Ordered list ---
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      const bk = k++;
      blocks.push(
        <ol key={`ol-${bk}`} className="cm-list">
          {items.map((it, ii) => <li key={ii}>{renderInline(it, `oli-${bk}-${ii}`)}</li>)}
        </ol>
      );
      continue;
    }

    // --- Blank line ---
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // --- Paragraph: gather consecutive non-structural lines ---
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('```') &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !(isTableRow(lines[i]) && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i += 1;
    }
    if (para.length) {
      const bk = k++;
      const nodes = [];
      para.forEach((p, pi) => {
        if (pi > 0) nodes.push(<br key={`br-${bk}-${pi}`} />);
        const inline = renderInline(p, `p-${bk}-${pi}`);
        if (Array.isArray(inline)) nodes.push(...inline);
        else nodes.push(inline);
      });
      blocks.push(<p key={`p-${bk}`} className="cm-paragraph">{nodes}</p>);
    }
  }

  return blocks;
}

const ChatMarkdown = ({ text }) => (
  <div className="cm-root">{renderMarkdown(text)}</div>
);

export default ChatMarkdown;
